import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Archive,
  ArrowLeft,
  Check,
  ChevronRight,
  Image,
  Plus,
  RefreshCcw,
  RotateCcw,
  Save,
  Search,
  Trash2,
  Utensils,
  X,
} from "lucide-react";

import {
  NovaCmsQrManager,
} from "./NovaCmsQrManager";

import {
  uploadNovaMenuItemImage,
} from "../media/nova-media";

import {
  loadNovaCmsMenuCatalog,
  removeNovaCmsMenuModifier,
  removeNovaCmsMenuVariant,
  saveNovaCmsMenuCategory,
  saveNovaCmsMenuItem,
  saveNovaCmsMenuModifier,
  saveNovaCmsMenuVariant,
  setNovaCmsMenuItemArchived,
  type NovaCmsMenuCatalog,
  type NovaCmsMenuCategory,
  type NovaCmsMenuGroup,
  type NovaCmsMenuItem,
  type NovaCmsMenuModifier,
  type NovaCmsMenuVariant,
} from "./nova-cms-menu";

type GroupFilter =
  | "all"
  | NovaCmsMenuGroup;

type ArchiveFilter =
  | "active"
  | "archived";

function slugify(
  value: string,
) {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(
      /[\u0300-\u036f]/g,
      "",
    )
    .replace(
      /[^a-z0-9]+/g,
      "-",
    )
    .replace(
      /^-+|-+$/g,
      ""
    )
    .slice(
      0,
      64,
    );
}

function uniqueId(
  base: string,
  existing: string[],
) {
  const clean =
    slugify(base) ||
    `item-${Date.now()}`;

  if (
    !existing.includes(
      clean,
    )
  ) {
    return clean;
  }

  let index = 2;

  while (
    existing.includes(
      `${clean}-${index}`,
    )
  ) {
    index += 1;
  }

  return `${clean}-${index}`;
}

function money(
  value: number | null,
) {
  if (value === null) {
    return "Variants";
  }

  return new Intl.NumberFormat(
    "en-KE",
    {
      style: "currency",
      currency: "KES",
      maximumFractionDigits: 0,
    },
  ).format(value);
}

function isArchived(
  item: NovaCmsMenuItem,
) {
  return (
    !item.is_published &&
    !item.is_available &&
    !item.ordering_enabled &&
    !item.website_visible &&
    !item.qr_visible
  );
}

function MenuPhotographyField({
  itemId,
  itemName,
  imageUrl,
  disabled,
  onChange,
  onError,
}: {
  itemId: string;
  itemName: string;
  imageUrl: string | null;
  disabled: boolean;
  onChange: (
    imageUrl: string | null,
  ) => void;
  onError: (
    message: string,
  ) => void;
}) {
  const [
    uploading,
    setUploading,
  ] = useState(false);

  async function upload(
    file: File | null,
  ) {
    if (!file) {
      return;
    }

    setUploading(true);

    try {
      const url =
        await uploadNovaMenuItemImage(
          itemId,
          file,
        );

      onChange(url);
    } catch (
      uploadError
    ) {
      onError(
        uploadError instanceof Error
          ? uploadError.message
          : "Could not upload menu photography.",
      );
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="border border-[#b5bbae] bg-[#eee9dc]/40 p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
            Menu photography
          </p>

          <p className="mt-1 text-[10px] leading-4 text-[#718076]">
            JPG, PNG or WebP · maximum 8 MB
          </p>
        </div>

        <Image
          size={17}
          className="shrink-0 text-[#9d725c]"
        />
      </div>

      <div className="mt-4 aspect-[4/3] overflow-hidden border border-[#b5bbae] bg-[#e4e1d7]">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={
              itemName
                ? `${itemName} menu photography`
                : "Menu item photography"
            }
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-5 text-center text-[#718076]">
            <Image
              size={24}
              strokeWidth={1.2}
            />

            <span className="text-[8px] font-semibold uppercase tracking-[0.16em]">
              Photography coming soon
            </span>
          </div>
        )}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <label
          className={`flex h-10 items-center justify-center border border-[#314a3b] px-3 text-center text-[8px] font-semibold uppercase tracking-[0.13em] ${
            disabled || uploading
              ? "cursor-not-allowed opacity-40"
              : "cursor-pointer text-[#314a3b] hover:bg-[#314a3b] hover:text-[#f3ead7]"
          }`}
        >
          {uploading
            ? "Uploading…"
            : imageUrl
              ? "Replace photo"
              : "Upload photo"}

          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={
              disabled ||
              uploading
            }
            className="hidden"
            onChange={(event) => {
              const file =
                event.target.files?.[0] ??
                null;

              void upload(file);

              event.currentTarget.value =
                "";
            }}
          />
        </label>

        <button
          type="button"
          disabled={
            disabled ||
            uploading ||
            !imageUrl
          }
          onClick={() =>
            onChange(null)
          }
          className="h-10 border border-[#b5bbae] px-3 text-[8px] font-semibold uppercase tracking-[0.13em] text-[#9d725c] disabled:cursor-not-allowed disabled:opacity-35"
        >
          Remove photo
        </button>
      </div>

      {imageUrl && (
        <p className="mt-3 text-[9px] leading-4 text-[#718076]">
          Photo selected. Use Save Menu Item to publish this image reference to the catalogue.
        </p>
      )}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (
    checked: boolean,
  ) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 border-b border-[#b5bbae]/40 py-3 last:border-0">
      <span className="text-[10px] font-semibold uppercase tracking-[0.13em] text-[#667067]">
        {label}
      </span>

      <button
        type="button"
        role="switch"
        aria-checked={
          checked
        }
        onClick={() =>
          onChange(
            !checked,
          )
        }
        className={[
          "relative h-6 w-11 rounded-full transition",
          checked
            ? "bg-[#314a3b]"
            : "bg-[#c9c7bb]",
        ].join(" ")}
      >
        <span
          className={[
            "absolute top-1 h-4 w-4 rounded-full bg-[#f6f1e8] transition",
            checked
              ? "left-6"
              : "left-1",
          ].join(" ")}
        />
      </button>
    </label>
  );
}

function ModalShell({
  title,
  eyebrow,
  onClose,
  children,
}: {
  title: string;
  eyebrow: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-[#18271e]/75 px-4 py-8">
      <div className="mx-auto w-full max-w-2xl border border-[#b5bbae] bg-[#f6f1e8] shadow-2xl">
        <div className="flex items-start justify-between border-b border-[#b5bbae] p-6">
          <div>
            <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#9d725c]">
              {eyebrow}
            </p>

            <h2 className="mt-2 font-['Cormorant_Garamond'] text-4xl text-[#314a3b]">
              {title}
            </h2>
          </div>

          <button
            type="button"
            onClick={
              onClose
            }
            className="flex h-10 w-10 items-center justify-center border border-[#b5bbae] text-[#718076]"
            aria-label="Close"
          >
            <X
              size={15}
            />
          </button>
        </div>

        {children}
      </div>
    </div>
  );
}

export function NovaCmsMenuManager() {
  const [
    qrManagerOpen,
    setQrManagerOpen,
  ] =
    useState(false);

  const [
    catalog,
    setCatalog,
  ] =
    useState<NovaCmsMenuCatalog>({
      categories: [],
      items: [],
      variants: [],
      modifiers: [],
    });

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    busy,
    setBusy,
  ] =
    useState(false);

  const [
    query,
    setQuery,
  ] =
    useState("");

  const [
    groupFilter,
    setGroupFilter,
  ] =
    useState<GroupFilter>(
      "all",
    );

  const [
    archiveFilter,
    setArchiveFilter,
  ] =
    useState<ArchiveFilter>(
      "active",
    );

  const [
    activeCategoryId,
    setActiveCategoryId,
  ] =
    useState<string | null>(
      null,
    );

  const [
    selectedItemId,
    setSelectedItemId,
  ] =
    useState<string | null>(
      null,
    );

  const [
    editingItem,
    setEditingItem,
  ] =
    useState<NovaCmsMenuItem | null>(
      null,
    );

  const [
    editingCategory,
    setEditingCategory,
  ] =
    useState<NovaCmsMenuCategory | null>(
      null,
    );

  const [
    newItem,
    setNewItem,
  ] =
    useState<NovaCmsMenuItem | null>(
      null,
    );

  const [
    editingVariant,
    setEditingVariant,
  ] =
    useState<NovaCmsMenuVariant | null>(
      null,
    );

  const [
    editingModifier,
    setEditingModifier,
  ] =
    useState<NovaCmsMenuModifier | null>(
      null,
    );

  const [
    message,
    setMessage,
  ] =
    useState<string | null>(
      null,
    );

  const [
    error,
    setError,
  ] =
    useState<string | null>(
      null,
    );

  async function load() {
    setLoading(true);
    setError(null);

    try {
      const next =
        await loadNovaCmsMenuCatalog();

      setCatalog(
        next,
      );

      if (
        activeCategoryId &&
        !next.categories.some(
          (category) =>
            category.id ===
            activeCategoryId,
        )
      ) {
        setActiveCategoryId(
          null,
        );
      }
    } catch (
      loadError
    ) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Could not load menu.",
      );
    }

    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (
      !selectedItemId
    ) {
      setEditingItem(
        null,
      );
      return;
    }

    const selected =
      catalog.items.find(
        (item) =>
          item.id ===
          selectedItemId,
      );

    setEditingItem(
      selected
        ? {
            ...selected,
          }
        : null,
    );
  }, [
    selectedItemId,
    catalog.items,
  ]);

  const categories =
    useMemo(
      () =>
        catalog.categories.filter(
          (category) =>
            groupFilter ===
              "all" ||
            category.group_name ===
              groupFilter,
        ),
      [
        catalog.categories,
        groupFilter,
      ],
    );

  const visibleItems =
    useMemo(() => {
      const normalized =
        query
          .trim()
          .toLowerCase();

      return catalog.items.filter(
        (item) => {
          const category =
            catalog.categories.find(
              (candidate) =>
                candidate.id ===
                item.category_id,
            );

          if (
            archiveFilter ===
              "active" &&
            isArchived(
              item,
            )
          ) {
            return false;
          }

          if (
            archiveFilter ===
              "archived" &&
            !isArchived(
              item,
            )
          ) {
            return false;
          }

          if (
            groupFilter !==
              "all" &&
            category?.group_name !==
              groupFilter
          ) {
            return false;
          }

          if (
            activeCategoryId &&
            item.category_id !==
              activeCategoryId
          ) {
            return false;
          }

          if (
            normalized &&
            ![
              item.name,
              item.description ??
                "",
              category?.name ??
                "",
            ]
              .join(" ")
              .toLowerCase()
              .includes(
                normalized,
              )
          ) {
            return false;
          }

          return true;
        },
      );
    }, [
      catalog,
      query,
      groupFilter,
      archiveFilter,
      activeCategoryId,
    ]);

  const totalAvailable =
    catalog.items.filter(
      (item) =>
        item.is_available &&
        !isArchived(
          item,
        ),
    ).length;

  const liveItems =
    catalog.items.filter(
      (item) =>
        item.is_published &&
        (
          item.website_visible ||
          item.qr_visible
        ),
    ).length;

  const archivedItems =
    catalog.items.filter(
      isArchived,
    ).length;

  const itemVariants =
    editingItem
      ? catalog.variants.filter(
          (variant) =>
            variant.item_id ===
            editingItem.id,
        )
      : [];

  const itemModifiers =
    editingItem
      ? catalog.modifiers.filter(
          (modifier) =>
            modifier.item_id ===
              editingItem.id ||
            (
              modifier.category_id ===
                editingItem.category_id &&
              modifier.item_id ===
                null
            ),
        )
      : [];

  function clearMessages() {
    setError(
      null,
    );

    setMessage(
      null,
    );
  }

  async function saveItem(
    item:
      NovaCmsMenuItem,
    isNew = false,
  ) {
    setBusy(
      true,
    );

    clearMessages();

    try {
      await saveNovaCmsMenuItem(
        item,
      );

      await load();

      setSelectedItemId(
        item.id,
      );

      if (
        isNew
      ) {
        setNewItem(
          null,
        );
      }

      setMessage(
        `${item.name} saved to the live Nova catalogue.`,
      );
    } catch (
      saveError
    ) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "Could not save menu item.",
      );
    }

    setBusy(
      false,
    );
  }

  async function saveCategory() {
    if (
      !editingCategory
    ) {
      return;
    }

    setBusy(
      true,
    );

    clearMessages();

    try {
      await saveNovaCmsMenuCategory(
        editingCategory,
      );

      await load();

      setMessage(
        `${editingCategory.name} category saved.`,
      );

      setEditingCategory(
        null,
      );
    } catch (
      saveError
    ) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "Could not save category.",
      );
    }

    setBusy(
      false,
    );
  }

  async function archiveItem(
    item:
      NovaCmsMenuItem,
    archived: boolean,
  ) {
    const confirmed =
      window.confirm(
        archived
          ? `Archive "${item.name}"?\n\nIt will disappear from the website, QR menu and ordering, but its database record will be preserved.`
          : `Restore "${item.name}"?\n\nIt will become published, available and visible on the website and QR menu again.`,
      );

    if (
      !confirmed
    ) {
      return;
    }

    setBusy(
      true,
    );

    clearMessages();

    try {
      await setNovaCmsMenuItemArchived(
        item.id,
        archived,
      );

      await load();

      setSelectedItemId(
        null,
      );

      setMessage(
        archived
          ? `${item.name} archived safely.`
          : `${item.name} restored.`,
      );
    } catch (
      archiveError
    ) {
      setError(
        archiveError instanceof Error
          ? archiveError.message
          : "Could not update archive state.",
      );
    }

    setBusy(
      false,
    );
  }

  async function saveVariant() {
    if (
      !editingVariant
    ) {
      return;
    }

    setBusy(
      true,
    );

    clearMessages();

    try {
      await saveNovaCmsMenuVariant(
        editingVariant,
      );

      await load();

      setEditingVariant(
        null,
      );

      setMessage(
        "Menu variant saved.",
      );
    } catch (
      saveError
    ) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "Could not save menu variant.",
      );
    }

    setBusy(
      false,
    );
  }

  async function removeVariant(
    variant:
      NovaCmsMenuVariant,
  ) {
    const confirmed =
      window.confirm(
        `Remove variant "${variant.label}"?`,
      );

    if (
      !confirmed
    ) {
      return;
    }

    setBusy(
      true,
    );

    clearMessages();

    try {
      await removeNovaCmsMenuVariant(
        variant.item_id,
        variant.id,
      );

      await load();

      setMessage(
        "Variant removed.",
      );
    } catch (
      removeError
    ) {
      setError(
        removeError instanceof Error
          ? removeError.message
          : "Could not remove variant.",
      );
    }

    setBusy(
      false,
    );
  }

  async function saveModifier() {
    if (
      !editingModifier
    ) {
      return;
    }

    setBusy(
      true,
    );

    clearMessages();

    try {
      await saveNovaCmsMenuModifier(
        editingModifier,
      );

      await load();

      setEditingModifier(
        null,
      );

      setMessage(
        "Menu modifier saved.",
      );
    } catch (
      saveError
    ) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "Could not save menu modifier.",
      );
    }

    setBusy(
      false,
    );
  }

  async function removeModifier(
    modifier:
      NovaCmsMenuModifier,
  ) {
    const confirmed =
      window.confirm(
        `Remove modifier "${modifier.name}"?`,
      );

    if (
      !confirmed
    ) {
      return;
    }

    setBusy(
      true,
    );

    clearMessages();

    try {
      await removeNovaCmsMenuModifier(
        modifier.id,
      );

      await load();

      setMessage(
        "Modifier removed.",
      );
    } catch (
      removeError
    ) {
      setError(
        removeError instanceof Error
          ? removeError.message
          : "Could not remove modifier.",
      );
    }

    setBusy(
      false,
    );
  }

  function beginNewItem() {
    const categoryId =
      activeCategoryId ??
      catalog.categories[0]?.id ??
      "";

    const sameCategory =
      catalog.items.filter(
        (item) =>
          item.category_id ===
          categoryId,
      );

    const nextOrder =
      sameCategory.length ===
      0
        ? 10
        : Math.max(
            ...sameCategory.map(
              (item) =>
                item.sort_order,
            ),
          ) + 10;

    setNewItem({
      id: "",
      category_id:
        categoryId,
      name: "",
      description:
        null,
      base_price:
        null,
      image_url:
        null,
      sort_order:
        nextOrder,
      is_available:
        true,
      is_featured:
        false,
      ordering_enabled:
        true,
      website_visible:
        true,
      qr_visible:
        true,
      is_published:
        true,
    });
  }

  return (
    <main className="min-h-screen bg-[#eee9dc] text-[#314a3b]">
      {qrManagerOpen && (
        <NovaCmsQrManager
          onClose={() =>
            setQrManagerOpen(
              false,
            )
          }
        />
      )}
      <header className="border-b border-[#b5bbae]/60 bg-[#26392c] px-6 py-6 text-[#f3ead7] lg:px-10">
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-5">
          <div className="flex items-center gap-5">
            <button
              type="button"
              onClick={() => {
                window.location.href =
                  "/nova-hq";
              }}
              className="flex h-10 w-10 items-center justify-center border border-[#f3ead7]/20"
            >
              <ArrowLeft
                size={16}
              />
            </button>

            <div>
              <p className="text-[9px] font-semibold uppercase tracking-[0.24em] text-[#d6bd91]">
                Nova HQ · Menu
              </p>

              <h1 className="mt-1 font-['Cormorant_Garamond'] text-4xl font-light">
                The live menu.
              </h1>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() =>
                void load()
              }
              disabled={
                loading ||
                busy
              }
              className="flex h-10 items-center gap-2 border border-[#f3ead7]/20 px-4 text-[9px] font-semibold uppercase tracking-[0.16em] disabled:opacity-40"
            >
              <RefreshCcw
                size={13}
              />
              Refresh
            </button>

            <button
              type="button"
              onClick={
                beginNewItem
              }
              className="flex h-10 items-center gap-2 border border-[#d6bd91] px-4 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#d6bd91]"
            >
              <Plus
                size={13}
              />
              New item
            </button>

            <button
              type="button"
              onClick={() =>
                setQrManagerOpen(
                  true,
                )
              }
              className="flex h-10 items-center gap-2 border border-[#d6bd91] px-4 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#d6bd91]"
            >
              <span className="font-mono text-[13px]">
                ▦
              </span>
              QR codes
            </button>

            <button
              type="button"
              onClick={() => {
                window.open(
                  "/menu",
                  "_blank",
                );
              }}
              className="flex h-10 items-center gap-2 bg-[#d6bd91] px-4 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#26392c]"
            >
              <Utensils
                size={13}
              />
              View QR menu
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1440px] px-6 py-8 lg:px-10">
        <section className="grid gap-3 sm:grid-cols-4">
          <div className="border border-[#b5bbae] bg-[#f6f1e8] p-5">
            <p className="text-[9px] uppercase tracking-[0.18em] text-[#718076]">
              Menu items
            </p>
            <p className="mt-2 font-['Cormorant_Garamond'] text-4xl">
              {catalog.items.length}
            </p>
          </div>

          <div className="border border-[#b5bbae] bg-[#f6f1e8] p-5">
            <p className="text-[9px] uppercase tracking-[0.18em] text-[#718076]">
              Available
            </p>
            <p className="mt-2 font-['Cormorant_Garamond'] text-4xl">
              {totalAvailable}
            </p>
          </div>

          <div className="border border-[#b5bbae] bg-[#f6f1e8] p-5">
            <p className="text-[9px] uppercase tracking-[0.18em] text-[#718076]">
              Publicly visible
            </p>
            <p className="mt-2 font-['Cormorant_Garamond'] text-4xl">
              {liveItems}
            </p>
          </div>

          <div className="border border-[#b5bbae] bg-[#f6f1e8] p-5">
            <p className="text-[9px] uppercase tracking-[0.18em] text-[#718076]">
              Archived
            </p>
            <p className="mt-2 font-['Cormorant_Garamond'] text-4xl">
              {archivedItems}
            </p>
          </div>
        </section>

        {message && (
          <div className="mt-5 flex items-center gap-3 border border-[#718076]/30 bg-[#dfe3d7] px-5 py-4 text-xs">
            <Check
              size={15}
            />
            {message}
          </div>
        )}

        {error && (
          <div className="mt-5 border border-[#9d725c]/40 bg-[#ead8cc] px-5 py-4 text-xs text-[#6e4635]">
            {error}
          </div>
        )}

        <section className="mt-7 grid gap-5 xl:grid-cols-[250px_minmax(0,1fr)_420px]">
          <aside className="border border-[#b5bbae] bg-[#f6f1e8]">
            <div className="border-b border-[#b5bbae] p-5">
              <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#9d725c]">
                Menu chapters
              </p>

              <div className="mt-4 grid grid-cols-3 gap-2">
                {(
                  [
                    "all",
                    "food",
                    "drinks",
                  ] as const
                ).map(
                  (group) => (
                    <button
                      key={
                        group
                      }
                      type="button"
                      onClick={() => {
                        setGroupFilter(
                          group,
                        );
                        setActiveCategoryId(
                          null,
                        );
                      }}
                      className={[
                        "border px-2 py-2 text-[8px] font-semibold uppercase tracking-[0.12em]",
                        groupFilter ===
                        group
                          ? "border-[#314a3b] bg-[#314a3b] text-[#f3ead7]"
                          : "border-[#b5bbae] text-[#667067]",
                      ].join(" ")}
                    >
                      {group}
                    </button>
                  ),
                )}
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setArchiveFilter(
                      "active",
                    )
                  }
                  className={[
                    "border px-2 py-2 text-[8px] font-semibold uppercase tracking-[0.12em]",
                    archiveFilter ===
                    "active"
                      ? "border-[#314a3b] bg-[#314a3b] text-[#f3ead7]"
                      : "border-[#b5bbae] text-[#667067]",
                  ].join(" ")}
                >
                  Active
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setArchiveFilter(
                      "archived",
                    )
                  }
                  className={[
                    "border px-2 py-2 text-[8px] font-semibold uppercase tracking-[0.12em]",
                    archiveFilter ===
                    "archived"
                      ? "border-[#9d725c] bg-[#9d725c] text-white"
                      : "border-[#b5bbae] text-[#667067]",
                  ].join(" ")}
                >
                  Archived
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={() =>
                setActiveCategoryId(
                  null,
                )
              }
              className={[
                "flex w-full items-center justify-between border-b border-[#b5bbae]/50 px-5 py-4 text-left",
                activeCategoryId ===
                null
                  ? "bg-[#dfe3d7]"
                  : "",
              ].join(" ")}
            >
              <span className="text-xs font-semibold">
                All menu items
              </span>
            </button>

            {categories.map(
              (category) => {
                const count =
                  catalog.items.filter(
                    (item) =>
                      item.category_id ===
                      category.id,
                  ).length;

                return (
                  <div
                    key={
                      category.id
                    }
                    className={[
                      "border-b border-[#b5bbae]/50 last:border-b-0",
                      activeCategoryId ===
                      category.id
                        ? "bg-[#dfe3d7]"
                        : "",
                    ].join(" ")}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setActiveCategoryId(
                          category.id,
                        )
                      }
                      className="flex w-full items-center justify-between px-5 pb-2 pt-4 text-left"
                    >
                      <div>
                        <p className="text-xs font-semibold">
                          {
                            category.name
                          }
                        </p>

                        <p className="mt-1 text-[8px] uppercase tracking-[0.14em] text-[#718076]">
                          {
                            category.group_name
                          }
                          {!category.is_published &&
                            " · hidden"}
                        </p>
                      </div>

                      <span className="text-[9px] text-[#718076]">
                        {count}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setEditingCategory({
                          ...category,
                        })
                      }
                      className="px-5 pb-4 text-[8px] font-semibold uppercase tracking-[0.14em] text-[#9d725c] underline underline-offset-4"
                    >
                      Edit category
                    </button>
                  </div>
                );
              },
            )}
          </aside>

          <section className="min-w-0 border border-[#b5bbae] bg-[#f6f1e8]">
            <div className="border-b border-[#b5bbae] p-5">
              <div className="relative">
                <Search
                  size={15}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-[#718076]"
                />

                <input
                  value={query}
                  onChange={(
                    event,
                  ) =>
                    setQuery(
                      event.target.value,
                    )
                  }
                  placeholder="Search the Nova menu..."
                  className="h-11 w-full border border-[#b5bbae] bg-[#eee9dc] pl-11 pr-4 text-sm outline-none focus:border-[#9d725c]"
                />
              </div>
            </div>

            {loading ? (
              <div className="p-10 text-sm text-[#718076]">
                Loading live menu…
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between border-b border-[#b5bbae]/50 px-5 py-4">
                  <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#718076]">
                    {visibleItems.length} items
                  </p>

                  <p className="text-[8px] uppercase tracking-[0.14em] text-[#9d725c]">
                    Supabase live catalogue
                  </p>
                </div>

                {visibleItems.map(
                  (item) => {
                    const category =
                      catalog.categories.find(
                        (candidate) =>
                          candidate.id ===
                          item.category_id,
                      );

                    const selected =
                      item.id ===
                      selectedItemId;

                    return (
                      <button
                        type="button"
                        key={
                          item.id
                        }
                        onClick={() =>
                          setSelectedItemId(
                            item.id,
                          )
                        }
                        className={[
                          "grid w-full grid-cols-[1fr_auto] gap-5 border-b border-[#b5bbae]/40 px-5 py-5 text-left transition last:border-b-0",
                          selected
                            ? "bg-[#dfe3d7]"
                            : "hover:bg-[#eee9dc]",
                        ].join(" ")}
                      >
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-['Cormorant_Garamond'] text-2xl leading-none">
                              {
                                item.name
                              }
                            </p>

                            {isArchived(item) && (
                              <span className="bg-[#26392c] px-2 py-1 text-[7px] font-semibold uppercase tracking-[0.14em] text-white">
                                Archived
                              </span>
                            )}

                            {!item.is_available &&
                              !isArchived(item) && (
                                <span className="bg-[#9d725c] px-2 py-1 text-[7px] font-semibold uppercase tracking-[0.14em] text-white">
                                  Sold out
                                </span>
                              )}

                            {item.is_featured && (
                              <span className="bg-[#d6bd91] px-2 py-1 text-[7px] font-semibold uppercase tracking-[0.14em] text-[#26392c]">
                                Featured
                              </span>
                            )}
                          </div>

                          <p className="mt-2 text-[9px] uppercase tracking-[0.16em] text-[#718076]">
                            {
                              category?.name ??
                              item.category_id
                            }
                          </p>

                          {item.description && (
                            <p className="mt-3 line-clamp-2 max-w-2xl text-xs leading-5 text-[#667067]">
                              {
                                item.description
                              }
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-4">
                          <p className="whitespace-nowrap font-['Cormorant_Garamond'] text-xl">
                            {money(
                              item.base_price,
                            )}
                          </p>

                          <ChevronRight
                            size={15}
                            className="text-[#9d725c]"
                          />
                        </div>
                      </button>
                    );
                  },
                )}

                {visibleItems.length ===
                  0 && (
                  <div className="p-10 text-center text-sm text-[#718076]">
                    No menu items match this view.
                  </div>
                )}
              </div>
            )}
          </section>

          <aside className="border border-[#b5bbae] bg-[#f6f1e8]">
            {editingItem ? (
              <>
                <div className="border-b border-[#b5bbae] p-5">
                  <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#9d725c]">
                    Edit menu item
                  </p>

                  <h2 className="mt-2 font-['Cormorant_Garamond'] text-3xl">
                    {
                      editingItem.name
                    }
                  </h2>

                  <p className="mt-1 font-mono text-[8px] text-[#718076]">
                    {
                      editingItem.id
                    }
                  </p>
                </div>

                <div className="space-y-6 p-5">
                  <label className="block">
                    <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                      Name
                    </span>

                    <input
                      value={
                        editingItem.name
                      }
                      onChange={(
                        event,
                      ) =>
                        setEditingItem({
                          ...editingItem,
                          name:
                            event.target.value,
                        })
                      }
                      className="mt-2 h-11 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 text-sm outline-none focus:border-[#9d725c]"
                    />
                  </label>

                  <label className="block">
                    <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                      Description
                    </span>

                    <textarea
                      value={
                        editingItem.description ??
                        ""
                      }
                      onChange={(
                        event,
                      ) =>
                        setEditingItem({
                          ...editingItem,
                          description:
                            event.target.value ||
                            null,
                        })
                      }
                      rows={4}
                      className="mt-2 w-full resize-y border border-[#b5bbae] bg-[#eee9dc] px-3 py-3 text-sm leading-6 outline-none focus:border-[#9d725c]"
                    />
                  </label>

                  <MenuPhotographyField
                    itemId={
                      editingItem.id
                    }
                    itemName={
                      editingItem.name
                    }
                    imageUrl={
                      editingItem.image_url
                    }
                    disabled={
                      busy ||
                      !editingItem.id
                    }
                    onChange={(
                      imageUrl,
                    ) =>
                      setEditingItem({
                        ...editingItem,
                        image_url:
                          imageUrl,
                      })
                    }
                    onError={
                      setError
                    }
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <label>
                      <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                        Base price
                      </span>

                      <input
                        type="number"
                        min="0"
                        value={
                          editingItem.base_price ??
                          ""
                        }
                        onChange={(
                          event,
                        ) =>
                          setEditingItem({
                            ...editingItem,
                            base_price:
                              event.target.value ===
                              ""
                                ? null
                                : Number(
                                    event.target.value,
                                  ),
                          })
                        }
                        className="mt-2 h-11 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 text-sm"
                      />
                    </label>

                    <label>
                      <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                        Order
                      </span>

                      <input
                        type="number"
                        min="0"
                        value={
                          editingItem.sort_order
                        }
                        onChange={(
                          event,
                        ) =>
                          setEditingItem({
                            ...editingItem,
                            sort_order:
                              Number(
                                event.target.value,
                              ),
                          })
                        }
                        className="mt-2 h-11 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 text-sm"
                      />
                    </label>
                  </div>

                  <label className="block">
                    <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                      Category
                    </span>

                    <select
                      value={
                        editingItem.category_id
                      }
                      onChange={(
                        event,
                      ) =>
                        setEditingItem({
                          ...editingItem,
                          category_id:
                            event.target.value,
                        })
                      }
                      className="mt-2 h-11 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 text-sm"
                    >
                      {catalog.categories.map(
                        (category) => (
                          <option
                            key={
                              category.id
                            }
                            value={
                              category.id
                            }
                          >
                            {
                              category.name
                            }
                          </option>
                        ),
                      )}
                    </select>
                  </label>

                  <div className="border border-[#b5bbae] px-4">
                    <Toggle
                      label="Available"
                      checked={
                        editingItem.is_available
                      }
                      onChange={(
                        checked,
                      ) =>
                        setEditingItem({
                          ...editingItem,
                          is_available:
                            checked,
                        })
                      }
                    />

                    <Toggle
                      label="Featured"
                      checked={
                        editingItem.is_featured
                      }
                      onChange={(
                        checked,
                      ) =>
                        setEditingItem({
                          ...editingItem,
                          is_featured:
                            checked,
                        })
                      }
                    />

                    <Toggle
                      label="Ordering enabled"
                      checked={
                        editingItem.ordering_enabled
                      }
                      onChange={(
                        checked,
                      ) =>
                        setEditingItem({
                          ...editingItem,
                          ordering_enabled:
                            checked,
                        })
                      }
                    />

                    <Toggle
                      label="Website"
                      checked={
                        editingItem.website_visible
                      }
                      onChange={(
                        checked,
                      ) =>
                        setEditingItem({
                          ...editingItem,
                          website_visible:
                            checked,
                        })
                      }
                    />

                    <Toggle
                      label="QR menu"
                      checked={
                        editingItem.qr_visible
                      }
                      onChange={(
                        checked,
                      ) =>
                        setEditingItem({
                          ...editingItem,
                          qr_visible:
                            checked,
                        })
                      }
                    />

                    <Toggle
                      label="Published"
                      checked={
                        editingItem.is_published
                      }
                      onChange={(
                        checked,
                      ) =>
                        setEditingItem({
                          ...editingItem,
                          is_published:
                            checked,
                        })
                      }
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      void saveItem(
                        editingItem,
                      )
                    }
                    disabled={
                      busy ||
                      editingItem.name
                        .trim()
                        .length ===
                        0
                    }
                    className="flex h-12 w-full items-center justify-center gap-2 bg-[#314a3b] text-[9px] font-semibold uppercase tracking-[0.18em] text-[#f3ead7] disabled:opacity-40"
                  >
                    <Save
                      size={14}
                    />
                    Save menu item
                  </button>

                  <div className="border-t border-[#b5bbae] pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#9d725c]">
                          Variants
                        </p>

                        <p className="mt-1 text-[10px] text-[#718076]">
                          Sizes or price options.
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          const current =
                            catalog.variants.filter(
                              (variant) =>
                                variant.item_id ===
                                editingItem.id,
                            );

                          setEditingVariant({
                            id:
                              uniqueId(
                                "variant",
                                current.map(
                                  (variant) =>
                                    variant.id,
                                ),
                              ),
                            item_id:
                              editingItem.id,
                            label:
                              "",
                            price:
                              editingItem.base_price ??
                              0,
                            sort_order:
                              current.length ===
                              0
                                ? 10
                                : Math.max(
                                    ...current.map(
                                      (variant) =>
                                        variant.sort_order,
                                    ),
                                  ) + 10,
                            is_available:
                              true,
                            is_published:
                              true,
                          });
                        }}
                        className="flex items-center gap-1 text-[8px] font-semibold uppercase tracking-[0.13em] text-[#9d725c]"
                      >
                        <Plus size={12} />
                        Add
                      </button>
                    </div>

                    <div className="mt-4 space-y-2">
                      {itemVariants.map(
                        (variant) => (
                          <div
                            key={
                              `${variant.item_id}:${variant.id}`
                            }
                            className="flex items-center justify-between border border-[#b5bbae] px-3 py-3"
                          >
                            <button
                              type="button"
                              onClick={() =>
                                setEditingVariant({
                                  ...variant,
                                })
                              }
                              className="min-w-0 text-left"
                            >
                              <p className="text-xs font-semibold">
                                {
                                  variant.label
                                }
                              </p>

                              <p className="mt-1 text-[9px] text-[#718076]">
                                {money(
                                  variant.price,
                                )}
                                {!variant.is_available &&
                                  " · unavailable"}
                              </p>
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                void removeVariant(
                                  variant,
                                )
                              }
                              className="text-[#9d725c]"
                              aria-label="Remove variant"
                            >
                              <Trash2
                                size={13}
                              />
                            </button>
                          </div>
                        ),
                      )}

                      {itemVariants.length ===
                        0 && (
                        <p className="border border-dashed border-[#b5bbae] px-3 py-4 text-[10px] text-[#718076]">
                          No variants. This item uses its base price.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-[#b5bbae] pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#9d725c]">
                          Modifiers
                        </p>

                        <p className="mt-1 text-[10px] text-[#718076]">
                          Extras attached to this item or its category.
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          const existingIds =
                            catalog.modifiers.map(
                              (modifier) =>
                                modifier.id,
                            );

                          setEditingModifier({
                            id:
                              uniqueId(
                                `${editingItem.id}-extra`,
                                existingIds,
                              ),
                            category_id:
                              null,
                            item_id:
                              editingItem.id,
                            name:
                              "",
                            price_delta:
                              0,
                            sort_order:
                              10,
                            is_available:
                              true,
                            is_published:
                              true,
                          });
                        }}
                        className="flex items-center gap-1 text-[8px] font-semibold uppercase tracking-[0.13em] text-[#9d725c]"
                      >
                        <Plus size={12} />
                        Add
                      </button>
                    </div>

                    <div className="mt-4 space-y-2">
                      {itemModifiers.map(
                        (modifier) => (
                          <div
                            key={
                              modifier.id
                            }
                            className="flex items-center justify-between border border-[#b5bbae] px-3 py-3"
                          >
                            <button
                              type="button"
                              onClick={() =>
                                setEditingModifier({
                                  ...modifier,
                                })
                              }
                              className="min-w-0 text-left"
                            >
                              <p className="text-xs font-semibold">
                                {
                                  modifier.name
                                }
                              </p>

                              <p className="mt-1 text-[9px] text-[#718076]">
                                {modifier.price_delta >=
                                0
                                  ? "+"
                                  : ""}
                                KES{" "}
                                {
                                  modifier.price_delta
                                }
                                {" · "}
                                {modifier.item_id
                                  ? "item"
                                  : "category"}
                              </p>
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                void removeModifier(
                                  modifier,
                                )
                              }
                              className="text-[#9d725c]"
                              aria-label="Remove modifier"
                            >
                              <Trash2
                                size={13}
                              />
                            </button>
                          </div>
                        ),
                      )}

                      {itemModifiers.length ===
                        0 && (
                        <p className="border border-dashed border-[#b5bbae] px-3 py-4 text-[10px] text-[#718076]">
                          No modifiers attached.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-[#b5bbae] pt-6">
                    {isArchived(
                      editingItem,
                    ) ? (
                      <button
                        type="button"
                        onClick={() =>
                          void archiveItem(
                            editingItem,
                            false,
                          )
                        }
                        className="flex h-11 w-full items-center justify-center gap-2 border border-[#314a3b] text-[9px] font-semibold uppercase tracking-[0.16em]"
                      >
                        <RotateCcw
                          size={13}
                        />
                        Restore item
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          void archiveItem(
                            editingItem,
                            true,
                          )
                        }
                        className="flex h-11 w-full items-center justify-center gap-2 border border-[#9d725c] text-[9px] font-semibold uppercase tracking-[0.16em] text-[#9d725c]"
                      >
                        <Archive
                          size={13}
                        />
                        Archive item
                      </button>
                    )}

                    <p className="mt-3 text-[9px] leading-5 text-[#718076]">
                      Archiving preserves the database record but removes the item from public menus and ordering.
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex min-h-[420px] items-center justify-center p-8 text-center">
                <div>
                  <Utensils
                    size={24}
                    className="mx-auto text-[#9d725c]"
                  />

                  <p className="mt-5 font-['Cormorant_Garamond'] text-2xl">
                    Choose a menu item.
                  </p>

                  <p className="mt-3 text-xs leading-6 text-[#718076]">
                    Edit its copy, pricing, availability, variants and modifiers here.
                  </p>
                </div>
              </div>
            )}
          </aside>
        </section>
      </div>

      {newItem && (
        <ModalShell
          eyebrow="New menu item"
          title={
            newItem.name ||
            "Add to the menu."
          }
          onClose={() =>
            setNewItem(
              null,
            )
          }
        >
          <div className="space-y-5 p-6">
            <label className="block">
              <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                Name
              </span>

              <input
                autoFocus
                value={
                  newItem.name
                }
                onChange={(
                  event,
                ) => {
                  const name =
                    event.target.value;

                  setNewItem({
                    ...newItem,
                    name,
                    id:
                      uniqueId(
                        name,
                        catalog.items.map(
                          (item) =>
                            item.id,
                        ),
                      ),
                  });
                }}
                className="mt-2 h-11 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 text-sm"
              />
            </label>

            <p className="font-mono text-[9px] text-[#718076]">
              ID:{" "}
              {newItem.id ||
                "generated from name"}
            </p>

            <label className="block">
              <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                Description
              </span>

              <textarea
                value={
                  newItem.description ??
                  ""
                }
                onChange={(
                  event,
                ) =>
                  setNewItem({
                    ...newItem,
                    description:
                      event.target.value ||
                      null,
                  })
                }
                rows={4}
                className="mt-2 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 py-3 text-sm"
              />
            </label>

            <div className="grid gap-4 sm:grid-cols-3">
              <label>
                <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                  Category
                </span>

                <select
                  value={
                    newItem.category_id
                  }
                  onChange={(
                    event,
                  ) =>
                    setNewItem({
                      ...newItem,
                      category_id:
                        event.target.value,
                    })
                  }
                  className="mt-2 h-11 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 text-sm"
                >
                  {catalog.categories.map(
                    (category) => (
                      <option
                        key={
                          category.id
                        }
                        value={
                          category.id
                        }
                      >
                        {
                          category.name
                        }
                      </option>
                    ),
                  )}
                </select>
              </label>

              <label>
                <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                  Price KES
                </span>

                <input
                  type="number"
                  min="0"
                  value={
                    newItem.base_price ??
                    ""
                  }
                  onChange={(
                    event,
                  ) =>
                    setNewItem({
                      ...newItem,
                      base_price:
                        event.target.value ===
                        ""
                          ? null
                          : Number(
                              event.target.value,
                            ),
                    })
                  }
                  className="mt-2 h-11 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 text-sm"
                />
              </label>

              <label>
                <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                  Order
                </span>

                <input
                  type="number"
                  min="0"
                  value={
                    newItem.sort_order
                  }
                  onChange={(
                    event,
                  ) =>
                    setNewItem({
                      ...newItem,
                      sort_order:
                        Number(
                          event.target.value,
                        ),
                    })
                  }
                  className="mt-2 h-11 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 text-sm"
                />
              </label>
            </div>

            <button
              type="button"
              disabled={
                busy ||
                !newItem.id ||
                !newItem.name.trim() ||
                !newItem.category_id
              }
              onClick={() =>
                void saveItem(
                  newItem,
                  true,
                )
              }
              className="flex h-12 w-full items-center justify-center gap-2 bg-[#314a3b] text-[9px] font-semibold uppercase tracking-[0.18em] text-[#f3ead7] disabled:opacity-40"
            >
              <Plus
                size={14}
              />
              Create menu item
            </button>
          </div>
        </ModalShell>
      )}

      {editingVariant && (
        <ModalShell
          eyebrow="Menu variant"
          title={
            editingVariant.label ||
            "Price option."
          }
          onClose={() =>
            setEditingVariant(
              null,
            )
          }
        >
          <div className="space-y-5 p-6">
            <label className="block">
              <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                Label
              </span>

              <input
                autoFocus
                value={
                  editingVariant.label
                }
                onChange={(
                  event,
                ) => {
                  const label =
                    event.target.value;

                  setEditingVariant({
                    ...editingVariant,
                    label,
                    id:
                      editingVariant.label ===
                        ""
                        ? uniqueId(
                            label,
                            itemVariants.map(
                              (variant) =>
                                variant.id,
                            ),
                          )
                        : editingVariant.id,
                  });
                }}
                className="mt-2 h-11 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 text-sm"
              />
            </label>

            <MenuPhotographyField
              itemId={
                newItem?.id ?? ""
              }
              itemName={
                newItem?.name ?? ""
              }
              imageUrl={
                newItem?.image_url ??
                null
              }
              disabled={
                busy ||
                !newItem?.id
              }
              onChange={(
                imageUrl,
              ) =>
                setNewItem(
                  (current) =>
                    current
                      ? {
                          ...current,
                          image_url:
                            imageUrl,
                        }
                      : current,
                )
              }
              onError={
                setError
              }
            />

            <div className="grid grid-cols-2 gap-4">
              <label>
                <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                  Price KES
                </span>

                <input
                  type="number"
                  min="0"
                  value={
                    editingVariant.price
                  }
                  onChange={(
                    event,
                  ) =>
                    setEditingVariant({
                      ...editingVariant,
                      price:
                        Number(
                          event.target.value,
                        ),
                    })
                  }
                  className="mt-2 h-11 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 text-sm"
                />
              </label>

              <label>
                <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                  Order
                </span>

                <input
                  type="number"
                  min="0"
                  value={
                    editingVariant.sort_order
                  }
                  onChange={(
                    event,
                  ) =>
                    setEditingVariant({
                      ...editingVariant,
                      sort_order:
                        Number(
                          event.target.value,
                        ),
                    })
                  }
                  className="mt-2 h-11 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 text-sm"
                />
              </label>
            </div>

            <div className="border border-[#b5bbae] px-4">
              <Toggle
                label="Available"
                checked={
                  editingVariant.is_available
                }
                onChange={(
                  checked,
                ) =>
                  setEditingVariant({
                    ...editingVariant,
                    is_available:
                      checked,
                  })
                }
              />

              <Toggle
                label="Published"
                checked={
                  editingVariant.is_published
                }
                onChange={(
                  checked,
                ) =>
                  setEditingVariant({
                    ...editingVariant,
                    is_published:
                      checked,
                  })
                }
              />
            </div>

            <button
              type="button"
              disabled={
                busy ||
                !editingVariant.label.trim()
              }
              onClick={() =>
                void saveVariant()
              }
              className="flex h-12 w-full items-center justify-center gap-2 bg-[#314a3b] text-[9px] font-semibold uppercase tracking-[0.18em] text-[#f3ead7] disabled:opacity-40"
            >
              <Save
                size={14}
              />
              Save variant
            </button>
          </div>
        </ModalShell>
      )}

      {editingModifier && (
        <ModalShell
          eyebrow="Menu modifier"
          title={
            editingModifier.name ||
            "Add an extra."
          }
          onClose={() =>
            setEditingModifier(
              null,
            )
          }
        >
          <div className="space-y-5 p-6">
            <label className="block">
              <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                Name
              </span>

              <input
                autoFocus
                value={
                  editingModifier.name
                }
                onChange={(
                  event,
                ) => {
                  const name =
                    event.target.value;

                  setEditingModifier({
                    ...editingModifier,
                    name,
                  });
                }}
                className="mt-2 h-11 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 text-sm"
              />
            </label>

            <div className="grid grid-cols-2 gap-4">
              <label>
                <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                  Price change KES
                </span>

                <input
                  type="number"
                  value={
                    editingModifier.price_delta
                  }
                  onChange={(
                    event,
                  ) =>
                    setEditingModifier({
                      ...editingModifier,
                      price_delta:
                        Number(
                          event.target.value,
                        ),
                    })
                  }
                  className="mt-2 h-11 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 text-sm"
                />
              </label>

              <label>
                <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                  Order
                </span>

                <input
                  type="number"
                  min="0"
                  value={
                    editingModifier.sort_order
                  }
                  onChange={(
                    event,
                  ) =>
                    setEditingModifier({
                      ...editingModifier,
                      sort_order:
                        Number(
                          event.target.value,
                        ),
                    })
                  }
                  className="mt-2 h-11 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 text-sm"
                />
              </label>
            </div>

            <label className="block">
              <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                Applies to
              </span>

              <select
                value={
                  editingModifier.item_id
                    ? `item:${editingModifier.item_id}`
                    : `category:${editingModifier.category_id ?? ""}`
                }
                onChange={(
                  event,
                ) => {
                  const [
                    type,
                    id,
                  ] =
                    event.target.value.split(
                      ":",
                    );

                  setEditingModifier({
                    ...editingModifier,
                    item_id:
                      type ===
                      "item"
                        ? id
                        : null,

                    category_id:
                      type ===
                      "category"
                        ? id
                        : null,
                  });
                }}
                className="mt-2 h-11 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 text-sm"
              >
                {editingItem && (
                  <option
                    value={`item:${editingItem.id}`}
                  >
                    This item — {editingItem.name}
                  </option>
                )}

                {catalog.categories.map(
                  (category) => (
                    <option
                      key={
                        category.id
                      }
                      value={`category:${category.id}`}
                    >
                      Category — {category.name}
                    </option>
                  ),
                )}
              </select>
            </label>

            <div className="border border-[#b5bbae] px-4">
              <Toggle
                label="Available"
                checked={
                  editingModifier.is_available
                }
                onChange={(
                  checked,
                ) =>
                  setEditingModifier({
                    ...editingModifier,
                    is_available:
                      checked,
                  })
                }
              />

              <Toggle
                label="Published"
                checked={
                  editingModifier.is_published
                }
                onChange={(
                  checked,
                ) =>
                  setEditingModifier({
                    ...editingModifier,
                    is_published:
                      checked,
                  })
                }
              />
            </div>

            <button
              type="button"
              disabled={
                busy ||
                !editingModifier.name.trim()
              }
              onClick={() =>
                void saveModifier()
              }
              className="flex h-12 w-full items-center justify-center gap-2 bg-[#314a3b] text-[9px] font-semibold uppercase tracking-[0.18em] text-[#f3ead7] disabled:opacity-40"
            >
              <Save
                size={14}
              />
              Save modifier
            </button>
          </div>
        </ModalShell>
      )}

      {editingCategory && (
        <ModalShell
          eyebrow="Edit menu category"
          title={
            editingCategory.name
          }
          onClose={() =>
            setEditingCategory(
              null,
            )
          }
        >
          <div className="space-y-5 p-6">
            <label className="block">
              <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                Category name
              </span>

              <input
                value={
                  editingCategory.name
                }
                onChange={(
                  event,
                ) =>
                  setEditingCategory({
                    ...editingCategory,
                    name:
                      event.target.value,
                  })
                }
                className="mt-2 h-11 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 text-sm"
              />
            </label>

            <label className="block">
              <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                Description
              </span>

              <textarea
                value={
                  editingCategory.description ??
                  ""
                }
                onChange={(
                  event,
                ) =>
                  setEditingCategory({
                    ...editingCategory,
                    description:
                      event.target.value ||
                      null,
                  })
                }
                rows={3}
                className="mt-2 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 py-3 text-sm"
              />
            </label>

            <div className="grid grid-cols-2 gap-4">
              <label>
                <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                  Group
                </span>

                <select
                  value={
                    editingCategory.group_name
                  }
                  onChange={(
                    event,
                  ) =>
                    setEditingCategory({
                      ...editingCategory,
                      group_name:
                        event.target.value as NovaCmsMenuGroup,
                    })
                  }
                  className="mt-2 h-11 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 text-sm"
                >
                  <option value="food">
                    Food
                  </option>
                  <option value="drinks">
                    Drinks
                  </option>
                </select>
              </label>

              <label>
                <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                  Order
                </span>

                <input
                  type="number"
                  min="0"
                  value={
                    editingCategory.sort_order
                  }
                  onChange={(
                    event,
                  ) =>
                    setEditingCategory({
                      ...editingCategory,
                      sort_order:
                        Number(
                          event.target.value,
                        ),
                    })
                  }
                  className="mt-2 h-11 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 text-sm"
                />
              </label>
            </div>

            <div className="border border-[#b5bbae] px-4">
              <Toggle
                label="Published"
                checked={
                  editingCategory.is_published
                }
                onChange={(
                  checked,
                ) =>
                  setEditingCategory({
                    ...editingCategory,
                    is_published:
                      checked,
                  })
                }
              />
            </div>

            <button
              type="button"
              onClick={() =>
                void saveCategory()
              }
              disabled={
                busy ||
                !editingCategory.name.trim()
              }
              className="flex h-12 w-full items-center justify-center gap-2 bg-[#314a3b] text-[9px] font-semibold uppercase tracking-[0.18em] text-[#f3ead7] disabled:opacity-40"
            >
              <Save
                size={14}
              />
              Save category
            </button>
          </div>
        </ModalShell>
      )}
    </main>
  );
}
